import { Tldraw } from '@tldraw/tldraw'
import '@tldraw/tldraw/tldraw.css'
import { useState, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import api from '../services/api'

// Helper functions to reduce nesting depth
const getShapesFromSnapshot = (snapshot) => {
  // Handle both snapshot formats:
  // 1. {store, schema} - from SWOT generation
  // 2. {document: {store}, session} - from editor.getSnapshot()
  const store = snapshot?.store || snapshot?.document?.store
  if (!store) return []
  return Object.values(store).filter(item => item.typeName === 'shape')
}

const getShapeTypes = (shapes) => {
  return shapes.map(s => s.type)
}

// Migrate old whiteboard data to fix schema incompatibilities
// This handles cases where old tldraw versions saved shapes with missing required props
const migrateWhiteboardData = (data) => {
  if (!data?.store) return data

  const migratedStore = { ...data.store }
  let migrated = false

  for (const [key, value] of Object.entries(migratedStore)) {
    if (value?.typeName === 'shape' && value?.type === 'text' && value?.props) {
      // Fix missing textAlign - required in newer tldraw versions
      if (value.props.textAlign === undefined) {
        migratedStore[key] = {
          ...value,
          props: {
            ...value.props,
            textAlign: 'start'
          }
        }
        migrated = true
      }
    }
    // Fix missing opacity at shape level (NOT in props) - required for all shapes
    if (value?.typeName === 'shape' && value.opacity === undefined) {
      migratedStore[key] = {
        ...migratedStore[key],
        opacity: 1
      }
      migrated = true
    }
  }

  if (migrated) {
    console.log('Migrated whiteboard data to fix schema incompatibilities')
    return { ...data, store: migratedStore }
  }

  return data
}

// Custom error fallback component for tldraw
// This replaces tldraw's built-in error screen with one that can reset server-side data
function CustomErrorFallback({ error, ideaId, onReset }) {
  const [isResetting, setIsResetting] = useState(false)

  const handleReset = async () => {
    setIsResetting(true)
    try {
      await api.deleteWhiteboard(ideaId)
      // Call the onReset callback to trigger re-render with fresh data
      if (onReset) {
        onReset()
      }
    } catch (err) {
      console.error('Failed to reset whiteboard:', err)
      setIsResetting(false)
      alert('Failed to reset whiteboard. Please try again.')
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-900 p-8">
      <div className="text-center max-w-md">
        <div className="text-red-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">Whiteboard Error</h3>
        <p className="text-gray-400 mb-4">
          The whiteboard data appears to be corrupted and cannot be loaded.
        </p>
        <p className="text-gray-500 text-sm mb-6 font-mono text-xs break-all">
          {error?.message || 'Unknown error'}
        </p>
        <button
          onClick={handleReset}
          disabled={isResetting}
          className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center mx-auto gap-2"
        >
          {isResetting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Resetting...</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Reset Whiteboard</span>
            </>
          )}
        </button>
        <p className="text-gray-500 text-xs mt-4">
          This will clear all whiteboard data and start fresh.
        </p>
      </div>
    </div>
  )
}

CustomErrorFallback.propTypes = {
  error: PropTypes.object,
  ideaId: PropTypes.string.isRequired,
  onReset: PropTypes.func
}

function IdeaWhiteboard({ ideaId, whiteboardData, onSave, onReset, className = '' }) {
  const [isSaving, setIsSaving] = useState(false)
  const [resetKey, setResetKey] = useState(0)
  const editorRef = useRef(null)
  const saveTimeoutRef = useRef(null)

  // Migrate whiteboard data to fix any schema incompatibilities
  const migratedWhiteboardData = useMemo(() => {
    // After reset, use null/empty data
    if (resetKey > 0) return null

    // tldraw accepts two snapshot formats:
    // 1. {document, session} - from editor.getSnapshot() (user saves)
    // 2. {store, schema} - from store serialization (SWOT generation)
    // Accept both formats, only reject truly invalid data
    if (!whiteboardData) {
      return null
    }

    // Check if data has either valid format
    const hasDocumentFormat = whiteboardData.document !== undefined
    const hasStoreFormat = whiteboardData.store !== undefined

    if (!hasDocumentFormat && !hasStoreFormat) {
      console.log('IdeaWhiteboard: data has neither document nor store format, treating as empty')
      return null
    }

    return migrateWhiteboardData(whiteboardData)
  }, [whiteboardData, resetKey])

  // Handle reset - increment key to force re-mount with fresh data
  const handleReset = useCallback(() => {
    setResetKey(k => k + 1)
    if (onReset) {
      onReset()
    }
  }, [onReset])

  // Create tldraw components with custom error fallback
  const tldrawComponents = useMemo(() => ({
    ErrorFallback: ({ error }) => (
      <CustomErrorFallback error={error} ideaId={ideaId} onReset={handleReset} />
    )
  }), [ideaId, handleReset])

  // Auto-save with debounce
  const handleEditorMount = useCallback((editor) => {
    editorRef.current = editor

    // Force dark mode
    editor.user.updateUserPreferences({ colorScheme: 'dark' })

    // Track mount time - don't save within first 3 seconds to let data load
    const mountTime = Date.now()
    const MIN_TIME_BEFORE_SAVE = 3000

    // Listen for changes and debounce saves
    const handleChange = () => {
      // Skip saving during initial load period
      const timeSinceMount = Date.now() - mountTime
      if (timeSinceMount < MIN_TIME_BEFORE_SAVE) {
        console.log('Skipping save - still in initial load period:', timeSinceMount, 'ms since mount')
        return
      }

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        console.log('Starting whiteboard save for idea:', ideaId)
        setIsSaving(true)
        try {
          const snapshot = editor.getSnapshot()
          const shapes = getShapesFromSnapshot(snapshot)
          const shapeTypes = getShapeTypes(shapes)

          console.log('Got whiteboard snapshot:', {
            ideaId,
            shapeCount: shapes.length,
            shapeTypes,
            snapshot
          })

          // Don't save empty whiteboards - this could overwrite real data
          if (shapes.length === 0) {
            console.log('Skipping save - whiteboard has no shapes, might overwrite server data')
            return
          }

          await onSave(snapshot)
          console.log('Whiteboard save completed for idea:', ideaId)
        } catch (error) {
          console.error('Failed to save whiteboard:', error)
        } finally {
          setIsSaving(false)
        }
      }, 1000) // Debounce for 1 second
    }

    // Listen for all store changes (not just 'user' source)
    editor.store.listen(handleChange)
  }, [onSave, ideaId])

  return (
    <div className={`relative ${className}`}>
      {/* Save indicator */}
      {isSaving && (
        <div className="absolute top-4 right-4 z-10 bg-blue-900/30 text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-700">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            <span>Saving...</span>
          </div>
        </div>
      )}

      {/* Tldraw canvas */}
      <div className="w-full h-full border border-gray-700 rounded-xl overflow-hidden tldraw-dark">
        <Tldraw
          key={resetKey}
          snapshot={migratedWhiteboardData}
          onMount={handleEditorMount}
          components={tldrawComponents}
          inferDarkMode
        />
      </div>
    </div>
  )
}

IdeaWhiteboard.propTypes = {
  ideaId: PropTypes.string.isRequired,
  whiteboardData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  className: PropTypes.string
}

export default IdeaWhiteboard
